/**
 * Kết xuất bảng chấm công dạng ma trận: cột = từng ngày trong tháng.
 * @param {'detail'|'daily'|'actual_hours'} mode
 *   - detail: giờ vào–ra từng phiếu
 *   - daily: quy đổi ngày công — ≥8h → 1; >4h và <8h → 0.5; >0 và ≤4h → "không đủ giờ công"
 *   - actual_hours: giờ công thực tế nằm trong khung giờ làm việc chi nhánh
 */

export function parseMonthKey(ym) {
  if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return null;
  const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
  if (m < 1 || m > 12) return null;
  const lastDay = new Date(y, m, 0).getDate();
  const pad = (n) => String(n).padStart(2, "0");
  return {
    year: y,
    month: m,
    daysInMonth: lastDay,
    from_date: `${y}-${pad(m)}-01`,
    to_date: `${y}-${pad(m)}-${pad(lastDay)}`,
  };
}

function dayFromWorkDate(workDate) {
  if (!workDate) return null;
  const s = typeof workDate === "string" ? workDate : String(workDate);
  // Chỉ parse trực tiếp khi là chuỗi ngày thuần YYYY-MM-DD.
  // Nếu là datetime (có timezone như ...Z), phải parse Date để tránh lệch cột theo múi giờ.
  const dateOnly = s.match(/^\d{4}-\d{2}-(\d{2})$/);
  if (dateOnly) return parseInt(dateOnly[1], 10);
  const d = new Date(workDate);
  if (!Number.isNaN(d.getTime())) return d.getDate();
  return null;
}

function durationToMinutes(wd) {
  if (!wd) return 0;
  if (typeof wd === "object" && (wd.hours != null || wd.minutes != null)) {
    return (wd.hours || 0) * 60 + (wd.minutes || 0);
  }
  if (typeof wd === "string" && wd.includes(":")) {
    const p = wd.split(":");
    const h = parseInt(p[0], 10) || 0;
    const m = parseInt(p[1], 10) || 0;
    return h * 60 + m;
  }
  return 0;
}

function minutesToLabel(totalMin) {
  if (totalMin <= 0) return "";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${m}m`;
}

function parseTimeToMinute(t) {
  if (!t) return null;
  if (t instanceof Date && !Number.isNaN(t.getTime())) {
    return t.getHours() * 60 + t.getMinutes();
  }
  const raw = String(t).trim();
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function calcOverlapMinutes(inMin, outMin, startMin, endMin) {
  if (
    inMin == null || outMin == null || startMin == null || endMin == null ||
    outMin <= inMin || endMin <= startMin
  ) {
    return 0;
  }
  const start = Math.max(inMin, startMin);
  const end = Math.min(outMin, endMin);
  return end > start ? end - start : 0;
}

function buildActualIntervals(row) {
  const inMin = parseTimeToMinute(row.check_in_time);
  const outMin = parseTimeToMinute(row.check_out_time);

  const morningStart = parseTimeToMinute(row.morning_start);
  const morningEnd = parseTimeToMinute(row.morning_end);
  const afternoonStart = parseTimeToMinute(row.afternoon_start);
  const afternoonEnd = parseTimeToMinute(row.afternoon_end);
  const hasBranchShift =
    (morningStart != null && morningEnd != null && morningEnd > morningStart) ||
    (afternoonStart != null && afternoonEnd != null && afternoonEnd > afternoonStart);

  // Mode actual_hours chỉ tính theo khung giờ ca chi nhánh.
  // Không có cấu hình ca hoặc thiếu check-in/check-out thì không tính.
  if (!hasBranchShift) {
    return { intervals: [], fallbackMin: 0 };
  }

  if (inMin == null || outMin == null || outMin <= inMin) {
    return { intervals: [], fallbackMin: 0 };
  }

  const intervals = [];
  if (morningStart != null && morningEnd != null && morningEnd > morningStart) {
    const s = Math.max(inMin, morningStart);
    const e = Math.min(outMin, morningEnd);
    if (e > s) intervals.push([s, e]);
  }
  if (afternoonStart != null && afternoonEnd != null && afternoonEnd > afternoonStart) {
    const s = Math.max(inMin, afternoonStart);
    const e = Math.min(outMin, afternoonEnd);
    if (e > s) intervals.push([s, e]);
  }
  return {
    intervals,
    fallbackMin: 0,
  };
}

function sumMergedIntervals(intervals) {
  if (!Array.isArray(intervals) || intervals.length === 0) return 0;
  const sorted = intervals
    .filter((it) => Array.isArray(it) && it.length === 2)
    .map(([s, e]) => [Number(s) || 0, Number(e) || 0])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return 0;

  let total = 0;
  let [curS, curE] = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const [s, e] = sorted[i];
    if (s <= curE) {
      curE = Math.max(curE, e);
    } else {
      total += curE - curS;
      curS = s;
      curE = e;
    }
  }
  total += curE - curS;
  return total;
}

function minutesToHourDecimal(totalMin) {
  if (!totalMin || totalMin <= 0) return "";
  const hours = totalMin / 60;
  return hours.toFixed(2);
}

function minutesToWorkdayDecimal(totalMin) {
  if (!totalMin || totalMin <= 0) return "";
  const workday = totalMin / (8 * 60);
  return workday.toFixed(2);
}

function capDailyActualMinutes(totalMin) {
  const safe = Number(totalMin) || 0;
  if (safe <= 0) return 0;
  return Math.min(safe, 8 * 60);
}

/** 8h = 480 phút, 4h = 240 phút */
function dayCreditFromMinutes(totalMin) {
  const M = Number(totalMin) || 0;
  if (M <= 0) return { display: "", credit: 0 };
  if (M >= 480) return { display: 1, credit: 1 };
  if (M > 240 && M < 480) return { display: 0.5, credit: 0.5 };
  return { display: "không đủ giờ công", credit: 0 };
}

/**
 * @returns {string[][]} — hàng đầu = header, các hàng sau = dữ liệu
 */
export function buildPivotAoA(records, year, month, mode) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const byEmp = new Map();

  (records || []).forEach((r) => {
    const dayNum = dayFromWorkDate(r.work_date);
    if (dayNum == null || dayNum < 1 || dayNum > daysInMonth) return;

    const eid = r.employee_id;
    if (!byEmp.has(eid)) {
      byEmp.set(eid, {
        employee_name: r.employee_name || "",
        branch_name: r.branch_name || "",
        department_name: r.department_name || "",
        days: {},
      });
    }
    const emp = byEmp.get(eid);
    if (!emp.days[dayNum]) {
      emp.days[dayNum] = { parts: [], totalMin: 0, actualIntervals: [], actualFallbackMin: 0 };
    }
    const cell = emp.days[dayNum];
    const cin = r.check_in_time ? String(r.check_in_time).slice(0, 5) : "";
    const cout = r.check_out_time ? String(r.check_out_time).slice(0, 5) : "";
    if (mode === "detail") {
      if (cin && cout) cell.parts.push(`${cin}-${cout}`);
      else if (cin) cell.parts.push(cout ? `${cin}→${cout}` : cin);
    }
    cell.totalMin += durationToMinutes(r.work_duration);
    const actual = buildActualIntervals(r);
    if (actual.intervals.length > 0) {
      cell.actualIntervals.push(...actual.intervals);
    }
    if (actual.fallbackMin > 0) {
      cell.actualFallbackMin += actual.fallbackMin;
    }
  });

  const dayHeaders = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    dayHeaders.push(String(d));
  }

  const header = [
    "Stt",
    "Chi nhánh",
    "Bộ phận",
    "Nhân viên",
    ...dayHeaders,
    mode === "daily"
      ? "Tổng ngày công (1 + 0.5)"
      : mode === "actual_hours"
        ? "Tổng giờ công thực tế"
        : "Tổng tháng",
    ...(mode === "actual_hours" ? ["Tổng ngày công thực tế"] : []),
  ];

  const rows = [header];
  let stt = 1;
  const sorted = Array.from(byEmp.entries()).sort((a, b) =>
    String(a[1].employee_name).localeCompare(String(b[1].employee_name), "vi")
  );

  sorted.forEach(([, emp]) => {
    let monthMin = 0;
    let monthCredit = 0;
    let monthActualMin = 0;
    const row = [
      stt,
      emp.branch_name,
      emp.department_name,
      emp.employee_name,
    ];
    for (let d = 1; d <= daysInMonth; d += 1) {
      const cell = emp.days[d];
      if (!cell) {
        row.push("");
        continue;
      }
      if (mode === "detail") {
        const uniq = [...new Set(cell.parts.filter(Boolean))];
        let text = uniq.join("; ");
        if (!text && cell.totalMin > 0) text = minutesToLabel(cell.totalMin);
        row.push(text);
        monthMin += cell.totalMin;
      } else if (mode === "actual_hours") {
        const mergedActualMin = sumMergedIntervals(cell.actualIntervals);
        const baseActualMin = mergedActualMin > 0 ? mergedActualMin : 0;
        const cappedActualMin = capDailyActualMinutes(baseActualMin);
        row.push(minutesToHourDecimal(cappedActualMin));
        monthActualMin += cappedActualMin;
      } else {
        const { display, credit } = dayCreditFromMinutes(cell.totalMin);
        row.push(display);
        monthCredit += credit;
      }
    }
    if (mode === "detail") {
      row.push(minutesToLabel(monthMin));
    } else if (mode === "actual_hours") {
      row.push(minutesToHourDecimal(monthActualMin));
      row.push(minutesToWorkdayDecimal(monthActualMin));
    } else {
      row.push(monthCredit > 0 ? monthCredit : "");
    }
    rows.push(row);
    stt += 1;
  });

  return rows;
}
